<template>
  <div class="edt">
    <v-app-bar
      flat
      color="transparent"
      class="main-appbar"
    >
      <app-bar-nav-icon />
      <div class="d-flex align-center">
        <v-toolbar-title>
          <h1 class="headline font-weight-bold">{{ pageTitle }}
          </h1>
        </v-toolbar-title>
      </div>
      <v-spacer />
      <div>
        <v-btn
          color="primary"
          class="mr-4"
          medium
          @click="onCancel"
          :disabled="form.$busy"
          text
          style="text-transform: none;"
        >Cancel</v-btn>
        <v-btn
          color="primary"
          medium
          @click="onSave"
          :loading="form.$busy"
          style="text-transform: none;"
        >Save</v-btn>
      </div>
    </v-app-bar>
    <v-row class="px-4 py-4">
      <v-col
        lg="8"
        md="6"
        sm="12"
        xs="12"
      >
        <v-card
          outlined
          class="px-6 py-8"
        >

          <v-text-field
            outlined
            flat
            label="Match ID"
            v-model="form.match_id"
            :error-messages="form.$getError('match_id')"
          />

          <v-text-field
            outlined
            flat
            label="Title"
            v-model="form.social_summary_title"
            :error-messages="form.$getError('social_summary_title')"
          />

          <vuetify-google-autocomplete
            outlined
            flat
            id="map"
            label="Venue"
            ref="form.venue"
            types="establishment"
            v-model="form.venue"
            :value="form.venue"
            :placeName="true"
            :enableGeolocation="true"
            :error-messages="form.$getError('venue')"
            @placechanged="getAddressData"
          >
          </vuetify-google-autocomplete>

          <v-textarea
            outlined
            name="input-7-4"
            label="Description"
            hint="${homeTeam}, ${visitingTeam}, ${venue} and ${matchDate} will be automaticall replaced by the actual team names based on the match ID"
            v-model="form.social_summary_description"
            :error-messages="form.$getError('social_summary_description')"
          />

          <content-editor
            label="Match Subheader"
            v-model="form.match_subheader"
            :error-messages="form.$getError('match_subheader')"
          />

          <image-file-input-viewer
            class="mt-6"
            v-model="form.thumbnail"
            :error-messages="form.$getError('thumbnail')"
            :label="'Image (used when page is shared on Social media)'"
          />
        </v-card>
      </v-col>
      <v-col
        lg="4"
        md="6"
        sm="12"
      >

        <published-date-selector
          pageTitle="Match Date"
          v-model="form.match_date"
        />
      </v-col>
    </v-row>
    <v-overlay
      absolute
      :value="updating"
    >
      <v-dialog
        v-model="updating"
        hide-overlay
        persistent
        width="300"
      >
        <v-card
          color="primary"
          dark
        >
          <v-card-text>
            {{ loading_message }}
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-overlay>
    <v-snackbar
      v-model="snackbar.show"
      right
      :color="snackbar.color"
    >
      {{ snackbar.message }}
      <v-btn
        text
        @click="snackbar.show = false"
      >OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import Form from '@/utils/form'
import get from 'lodash/get'
import BetHubPages from '@/models/BetHubPages'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import ImageFileInputViewer from '@/components/ImageFileInputViewer'
import { mapState, mapActions } from 'vuex'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import PublishedDateSelector from '@/views/Home/Articles/shared/PublishedDateSelector'
import has from 'lodash/has'
import isNull from 'lodash/isNull'
import dates from '@/utils/dates'
import ContentEditor from '@/components/ContentEditor'

const initialData = {
  match_id: null,
  social_summary_title: null,
  social_summary_description: null,
  match_subheader: null,
  venue: null,
  formatted_venue: null,
  country: null,
  city: null,
  lat: null,
  lng: null,
  match_date: null,
  thumbnail: null
}

export default {
  name: 'CreateBethubSeo',

  data () {
    return {
      loading: false,
      updating: false,
      loading_message: 'Loading',
      snackbar: {
        show: false,
        message: null,
        color: 'success'
      },
      loadingTags: false,
      form: new Form({
        ...initialData
      }),
      showDatePicker: false
    }
  },
  props: {
    betHubPage: {
      type: [BetHubPages, Object],
      default () {
        return {}
      }
    },
    pageTitle: {
      type: String,
      default: 'New Matches SEO Settings'
    },
    redirectCancelTo: {
      type: [String, Object],
      default () {
        return {
          name: 'bethub-pages'
        }
      }
    },
    redirectSaveTo: {
      type: [String, Object],
      default () {
        return {
          name: 'bethub-pages'
        }
      }
    }
  },

  components: {
    AppBarNavIcon,
    ImageFileInputViewer,
    PublishedDateSelector,
    ContentEditor
  },

  methods: {
    ...mapActions({
      updateRecord: 'betHubPages/update',
      generateSeo: 'betHubPages/generateSeo'
    }),

    onCancel () {
      this.$router.replace(this.redirectCancelTo)
    },

    /**
    * When the location found
    * @param {Object} addressData Data of the found location
    * @param {Object} placeResultData PlaceResult object
    * @param {String} id Input container ID
    */
    getAddressData (addressData, placeResultData, id) {
      this.form.country = addressData.country
      this.form.city = addressData.locality
      this.form.lat = addressData.latitude
      this.form.lng = addressData.longitude
      this.form.formatted_venue = `${placeResultData.name} ${placeResultData.formatted_address}`
    },

    async onSave () {
      try {
        this.form.$busy = true
        this.form.$clearErrors()
        const data = this.form.$data()

        if (has(data, 'match_date') && !isNull(data.match_date)) {
          data.match_date = dates.localDateToUTC(data.match_date).format('YYYY-MM-DD HH:mm:ss')
        }

        if (this.isModeUpdate) {
          let formData = new FormData()
          formData.append('id', data.id)
          formData.append('_method', 'put')
          formData.append('match_id', data.match_id)
          formData.append('social_summary_title', data.social_summary_title)
          formData.append('social_summary_description', data.social_summary_description)
          if (data.match_subheader) { formData.append('match_subheader', data.match_subheader) }
          if (data.venue) { formData.append('venue', data.venue) }

          if (data.match_date) { formData.append('match_date', data.match_date) }

          if (data.thumbnail && !data.thumbnail.id) { formData.append('thumbnail', data.thumbnail) }

          await this.updateRecord(formData)
        } else {
          const betHubPages = new BetHubPages(data)
          await betHubPages.save()
        }

        this.$router.replace(this.redirectSaveTo)
      } catch (error) {
        if (get(error, 'response.status') === 422) {
          if (get(error, 'response.data.errors') !== null) {
            this.form.$setErrors(get(error, 'response.data.errors'))
          } else {
            this.showSnackbar('Something went wrong.', 'error')
          }
        } else {
          this.showSnackbar(error, 'error')
        }
      } finally {
        this.form.$busy = false
      }
    },
    showSnackbar (message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },

    async generate () {
      try {
        this.loading = true
        const { data } = await this.generateSeo({ match_id: this.form.match_id })
        if (data) {
          this.form.social_summary_title = data.title
          this.form.social_summary_description = data.description
        }
      } catch (error) {
        this.showSnackbar(error.response.data.message, 'error')
      } finally {
        this.loading = false
      }
    }
  },

  watch: {
    betHubPage: {
      immediate: true,
      handler (val) {
        if (val) {
          this.form = new Form({
            ...initialData,
            ...val
          })
        }
      }
    }
  },

  computed: {

    ...mapState({
      recordList: state => state.pageSettings.list
    }),

    isModeUpdate () {
      return get(this.form, 'id', null) !== null
    }
  }
}
</script>
